import { atom, useRecoilState } from 'recoil';
import { message, notification } from 'antd';
import {
  Messages,
  useChatApi,
  SessionsByDate,
  Metadata,
  Session,
  CreateProjectParams,
  Project,
  CategorizedProjects,
  GroupedSessionsProject,
  EmbeddedWindowState,
  ProjectCategory
} from 'api/useChat';
import { trackChatMessageSent, trackChatMessageDelivered } from 'shared/observability/prometheus';
import { useAuth0 } from '@auth0/auth0-react';
import { useRef } from 'react';
import { scroller } from 'react-scroll';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { SessionFile } from 'api/useChat';

interface BaseDialogState {
  isOpen: boolean;
  category: string;
  nodeCategory: string;
  projectIndex: number;
  sessionId: string;
  sessionIndex: number;
  updatedAt: string;
}

interface RenameDialogState extends BaseDialogState {
  title: string;
}

interface RemoveDialogState extends BaseDialogState {
  projectId: string;
}

interface NewDealProjectDialogState {
  isOpen: boolean;
}

interface DialogState {
  rename: RenameDialogState;
  remove: RemoveDialogState;
  newDealProject: NewDealProjectDialogState;
}
interface PromptAreaData {
  vendor: { value: string; label: string }[];
  promptOptions: { [key: string]: string };
}
interface ChatState {
  activeKey: string;
  messages: Messages[];
  intermediateMessages: Messages | null;
  aiReport: { runId: string; content: string };
  isLoading: {
    isPageLoading: boolean;
    isStreaming: boolean;
    isMessageLoading: boolean;
    isSessionLoading: boolean;
    isUpdateingBookmark: boolean;
    isReportLoading: boolean;
  };
  currentSessionId: {
    id: string;
    metadata: Metadata;
    updatedAt: string;
    index: number;
    category: string;
    project: GroupedSessionsProject | null;
  } | null;
  bookmarks: Messages[];
  sessions: SessionsByDate;
  searchValue: string;
  embeddedWindowState: EmbeddedWindowState;
  projects: CategorizedProjects;
  dialogs: DialogState;
  isDocumentMode: boolean;
  promptMode: string | null;
  promptAreaData: PromptAreaData;
  reports: { [key: string]: string };
}

const initialState: ChatState = {
  activeKey: '1',
  messages: [],
  intermediateMessages: null,
  aiReport: { runId: '', content: '' },
  isLoading: {
    isPageLoading: false,
    isStreaming: false,
    isMessageLoading: false,
    isSessionLoading: false,
    isUpdateingBookmark: false,
    isReportLoading: false
  },
  currentSessionId: null,
  bookmarks: [],
  sessions: {},
  searchValue: '',
  embeddedWindowState: {
    isEmbeddedWindowOpen: false,
    isDrawerOpen: false,
    type: 'DEAL',
    data: {
      ContractId: null
    }
  },
  projects: {
    GENERAL: [],
    DEAL: [],
    SUPPLIER: [],
    CLIENT: []
  },
  dialogs: {
    rename: {
      isOpen: false,
      sessionId: '',
      title: '',
      sessionIndex: -1,
      updatedAt: '',
      category: '',
      nodeCategory: '',
      projectIndex: -1
    },
    remove: {
      isOpen: false,
      sessionId: '',
      sessionIndex: -1,
      updatedAt: '',
      category: '',
      projectId: '',
      nodeCategory: '',
      projectIndex: -1
    },
    newDealProject: {
      isOpen: false
    }
  },
  isDocumentMode: false,
  promptMode: null,
  promptAreaData: {
    vendor: [],
    promptOptions: {}
  },
  reports: {}
};
export type Mode = 'standalone' | 'embedded' | 'horizontal';

const clearQueryParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('contractId');
  url.searchParams.delete('sessionId');
  window.history.replaceState({}, '', url.toString());
};

export const chatState = atom<ChatState>({
  key: 'chatState',
  default: initialState
});

type ChatAction =
  | { type: 'SET_ACTIVE_KEY'; payload: string }
  | { type: 'SET_MESSAGES'; payload: Messages[] }
  | { type: 'ADD_MESSAGE'; payload: Messages }
  | {
      type: 'SET_LOADING';
      payload: {
        isPageLoading: boolean;
        isStreaming: boolean;
        isMessageLoading: boolean;
        isSessionLoading: boolean;
        isUpdateingBookmark: boolean;
        isReportLoading: boolean;
      };
    }
  | {
      type: 'SET_CURRENT_SESSION_ID';
      payload: {
        id: string;
        metadata: Metadata;
        updatedAt: string;
        index: number;
        category: string;
        project: GroupedSessionsProject | null;
      } | null;
    }
  | { type: 'SET_BOOKMARKS'; payload: Messages[] }
  | { type: 'SET_PROJECTS'; payload: CategorizedProjects }
  | { type: 'SET_SEARCH_VALUE'; payload: string }
  | { type: 'SEND_MESSAGE'; payload: { content: string; currentSessionId: string } }
  | { type: 'BOOKMARK_MESSAGE'; payload: { messageId: string; currentSessionId: string } }
  | { type: 'REMOVE_BOOKMARK'; payload: string }
  | { type: 'LOAD_INITIAL_SESSION' }
  | { type: 'LOAD_BOOKMARKS'; payload: string }
  | { type: 'SELECT_SESSION'; payload: string }
  | { type: 'UPDATE_MESSAGE'; payload: { id: string; content: string; metadata?: Metadata } }
  | { type: 'UPDATE_MESSAGE_ID'; payload: { oldId: string; newId: string } }
  | { type: 'SET_EMBEDDED_WINDOW_STATE'; payload: EmbeddedWindowState }
  | { type: 'CREATE_NEW_CONVERSATION'; payload: { project: GroupedSessionsProject } }
  | { type: 'OPEN_RENAME_DIALOG'; payload: RenameDialogState }
  | { type: 'CLOSE_RENAME_DIALOG' }
  | { type: 'OPEN_REMOVE_DIALOG'; payload: RemoveDialogState }
  | { type: 'CLOSE_REMOVE_DIALOG' }
  | { type: 'OPEN_NEW_DEAL_PROJECT_DIALOG' }
  | { type: 'CLOSE_NEW_DEAL_PROJECT_DIALOG' }
  | { type: 'SET_QUICK_CHECK_REPORTS'; payload: Messages[] }
  | { type: 'SET_IS_DOCUMENT_MODE'; payload: boolean }
  | { type: 'SET_PROMPT_MODE'; payload: string }
  | { type: 'ADD_INTERMEDIATE_MESSAGE'; payload: { message: Messages } }
  | { type: 'UPDATE_INTERMEDIATE_MESSAGE'; payload: { content: string } }
  | { type: 'UPDATE_AI_REPORT_RUN_ID'; payload: string }
  | { type: 'UPDATE_AI_REPORT_CONTENT'; payload: { content: string } }
  | { type: 'CLEAR_AI_REPORT' }
  | { type: 'CLEAR_INTERMEDIATE_MESSAGES' }
  | { type: 'SET_PROMPT_AREA_DATA'; payload: PromptAreaData }
  | { type: 'SET_REPORTS'; payload: { [key: string]: string } }
  | { type: 'UPDATE_REPORTS'; payload: { reportType: string; reportContent: string } };

/**
 * Reducer function for managing chat state
 * @param {ChatState} state - The current state of the chat
 * @param {ChatAction} action - The action to be performed on the state
 * @returns {ChatState} The new state after applying the action
 * @example
 * // Set active key
 * chatReducer(state, { type: 'SET_ACTIVE_KEY', payload: '2' })
 *
 * // Add a message
 * chatReducer(state, { type: 'ADD_MESSAGE', payload: newMessage })
 *
 * // Set loading state
 * chatReducer(state, { type: 'SET_LOADING', payload: true })
 */
function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_ACTIVE_KEY':
      return { ...state, activeKey: action.payload };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: { ...state.isLoading, ...action.payload }
      };
    case 'SET_CURRENT_SESSION_ID':
      return { ...state, currentSessionId: action.payload };
    case 'SET_BOOKMARKS':
      return { ...state, bookmarks: action.payload };
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload };
    case 'SET_SEARCH_VALUE':
      return { ...state, searchValue: action.payload };
    case 'REMOVE_BOOKMARK':
      return { ...state, bookmarks: state.bookmarks.filter((b) => b.id !== action.payload) };
    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map((message) =>
          message.id === action.payload.id
            ? { ...message, content: action.payload.content, metadata: action.payload.metadata as Messages['metadata'] }
            : message
        )
      };
    case 'UPDATE_MESSAGE_ID':
      return {
        ...state,
        messages: state.messages.map((message) =>
          message.id === action.payload.oldId ? { ...message, id: action.payload.newId } : message
        )
      };
    case 'CREATE_NEW_CONVERSATION':
      clearQueryParams();
      return {
        ...state,
        messages: [],
        bookmarks: [],
        currentSessionId: {
          id: '',
          metadata: {},
          updatedAt: new Date().toISOString(),
          index: -1,
          category: action.payload.project.category,
          project: action.payload.project
        },
        activeKey: '1'
      };
    case 'SET_EMBEDDED_WINDOW_STATE':
      return { ...state, embeddedWindowState: action.payload };
    case 'OPEN_RENAME_DIALOG':
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          rename: action.payload
        }
      };
    case 'CLOSE_RENAME_DIALOG':
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          rename: initialState.dialogs.rename
        }
      };
    case 'OPEN_REMOVE_DIALOG':
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          remove: action.payload
        }
      };
    case 'CLOSE_REMOVE_DIALOG':
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          remove: initialState.dialogs.remove
        }
      };
    case 'OPEN_NEW_DEAL_PROJECT_DIALOG':
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          newDealProject: { isOpen: true }
        }
      };
    case 'CLOSE_NEW_DEAL_PROJECT_DIALOG':
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          newDealProject: { isOpen: false }
        }
      };

    case 'SET_IS_DOCUMENT_MODE':
      return { ...state, isDocumentMode: action.payload };
    case 'SET_PROMPT_MODE':
      return { ...state, promptMode: action.payload };
    case 'ADD_INTERMEDIATE_MESSAGE':
      return { ...state, intermediateMessages: action.payload.message };
    case 'UPDATE_INTERMEDIATE_MESSAGE':
      return {
        ...state,
        intermediateMessages: { ...state.intermediateMessages, content: action.payload.content } as Messages
      };
    case 'CLEAR_INTERMEDIATE_MESSAGES':
      return { ...state, intermediateMessages: null };

    case 'UPDATE_AI_REPORT_RUN_ID':
      return { ...state, aiReport: { ...state.aiReport, runId: action.payload } };
    case 'UPDATE_AI_REPORT_CONTENT':
      return { ...state, aiReport: { ...state.aiReport, content: state.aiReport.content + action.payload.content } };
    case 'CLEAR_AI_REPORT':
      return { ...state, aiReport: { runId: '', content: '' } };
    case 'SET_PROMPT_AREA_DATA':
      return { ...state, promptAreaData: action.payload };
    case 'SET_REPORTS':
      return { ...state, reports: action.payload };
    case 'UPDATE_REPORTS':
      return { ...state, reports: { ...state.reports, [action.payload.reportType]: action.payload.reportContent } };
    default:
      return state;
  }
}

/**
 * Custom hook for managing chat state and operations
 * @returns {object} Chat state and operations
 */
export const useChatState = () => {
  const [state, setState] = useRecoilState(chatState);
  const [searchParams] = useSearchParams();
  const {
    getAllProjects,
    deleteSession,
    renameSession,
    renameProject,
    getAllChatsBySessionId,
    createSession,
    createProject,
    updateMessageAsBookmark,
    postChatQuery,
    deleteProject,
    getVendorInfo,
    getAllAssistants
  } = useChatApi();
  const { getAccessTokenSilently } = useAuth0();
  const abortControllerRef = useRef<AbortController | null>(null);

  const dispatch = (action: ChatAction) => {
    setState((prevState) => chatReducer(prevState, action));
  };

  /**
   * Deletes a chat session by its ID
   * @param {string} sessionId - The ID of the session to delete
   * @param updatedAt
   * @param projectIndex
   * @param sessionIndex
   * @param category
   * @returns {Promise<void>} A promise that resolves when the session is deleted
   * @throws {Error} If there's an error deleting the session
   * @description This function deletes a session from the backend and updates the local state.
   * If the deleted session is the current session, it creates a new conversation.
   */
  const deleteSessionItem = async (
    sessionId: string,
    updatedAt: string,
    projectIndex: number,
    sessionIndex: number,
    category?: string
  ) => {
    try {
      await deleteSession(sessionId);
      notification.success({
        message: 'Session Deleted',
        placement: 'topRight'
      });

      const projects = state.projects as CategorizedProjects;
      const categoryToUse = category || 'GENERAL';

      // Check if the category exists in projects
      if (!projects[categoryToUse as keyof CategorizedProjects]) {
        console.error(`Category ${categoryToUse} not found in projects`);
        return;
      }

      const categorizedProjectsArray = [...projects[categoryToUse as keyof CategorizedProjects]]; // category related projects
      const categorizedProject = categorizedProjectsArray[projectIndex]; // one particular project
      const categorizedProjectSessionsArr = [
        ...categorizedProject.chatSessions[moment(updatedAt).format('YYYY-MM-DD')]
      ]; // one project related updatedAt date sessions arr
      categorizedProjectSessionsArr.splice(sessionIndex, 1); //spliced array
      const categorizedProjectSessions = {
        ...categorizedProject.chatSessions,
        [moment(updatedAt).format('YYYY-MM-DD')]: categorizedProjectSessionsArr
      };

      const updatedCategorizedProject = {
        ...categorizedProjectsArray[projectIndex],
        chatSessions: categorizedProjectSessions
      };

      categorizedProjectsArray[projectIndex] = updatedCategorizedProject;

      const updatedProjects: CategorizedProjects = {
        ...projects,
        [categoryToUse]: categorizedProjectsArray
      };

      dispatch({ type: 'SET_PROJECTS', payload: updatedProjects });
      dispatch({ type: 'SET_BOOKMARKS', payload: [] });

      if (state.currentSessionId?.id === sessionId) {
        if (state.embeddedWindowState.isEmbeddedWindowOpen || state.embeddedWindowState.isDrawerOpen) {
          closeEmbeddedWindow();
        } else {
          await handleNewConversation();
        }
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      notification.error({
        message: 'Error deleting session',
        description: 'There was an error deleting the session. Please try again.',
        placement: 'topRight'
      });
    }
  };

  /**
   * Deletes a project and updates the local state accordingly
   * @param {string} projectIndex - The index of the project to delete
   * @param {string} [category] - Optional category of the project (defaults to 'GENERAL')
   * @returns {Promise<void>} A promise that resolves when the project is deleted
   * @throws {Error} If there's an error deleting the project
   * @description This function:
   * 1. Calls the API to delete the project
   * 2. Shows a success notification
   * 3. Updates the local state by removing the project from the specified category
   * 4. If the deleted project was the current project, creates a new conversation
   * 5. Shows an error notification if the deletion fails
   */
  const deleteProjectItem = async (projectIndex: number, category?: string) => {
    try {
      const projects = state.projects as CategorizedProjects;
      const categoryToUse = category || 'GENERAL';
      const project = projects[categoryToUse as keyof CategorizedProjects][projectIndex];
      await deleteProject(project.id);
      notification.success({
        message: 'Project Deleted',
        placement: 'topRight'
      });

      const categorizedProjectArray = [...projects[categoryToUse as keyof CategorizedProjects]];
      categorizedProjectArray.splice(projectIndex, 1);

      const updatedProjects: CategorizedProjects = {
        ...projects,
        [categoryToUse]: categorizedProjectArray
      };
      dispatch({ type: 'SET_PROJECTS', payload: updatedProjects });

      if (state.currentSessionId?.project?.id === project.id) {
        if (state.embeddedWindowState.isEmbeddedWindowOpen || state.embeddedWindowState.isDrawerOpen) {
          closeEmbeddedWindow();
        }
        dispatch({
          type: 'CREATE_NEW_CONVERSATION',
          payload: {
            project: state.projects.GENERAL[0]
          }
        });
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  /**
   * Renames a chat session by its ID
   * @param {string} sessionId - The ID of the session to rename
   * @param {string} newTitle - The new title for the session
   * @param sessionIndex
   * @param updatedAt
   * @param projectIndex
   * @param category
   * @returns {Promise<void>} A promise that resolves when the session is renamed
   * @throws {Error} If there's an error renaming the session
   * @description This function updates the session title in the backend and updates the local state.
   */
  const renameSessionItem = async (
    sessionId: string,
    newTitle: string,
    sessionIndex: number,
    updatedAt: string,
    projectIndex: number,
    category?: string
  ) => {
    try {
      await renameSession(sessionId, newTitle);
      notification.success({
        message: 'Session Renamed',
        placement: 'topRight'
      });

      const projects = state.projects as CategorizedProjects;
      const categoryToUse = category || 'GENERAL';
      const categorizedProjectsArray = [...projects[categoryToUse as keyof CategorizedProjects]];
      const project = categorizedProjectsArray[projectIndex];

      // Get the date key for the session
      const dateKey = moment(updatedAt).format('YYYY-MM-DD');
      const today = new Date().toISOString().split('T')[0];

      // Get the sessions array for the date key
      const sessionsArray = [...project.chatSessions[dateKey]];

      const todaysSessionsArray = [...(project.chatSessions[today] || [])];
      const removedSession =
        dateKey === today ? todaysSessionsArray.splice(sessionIndex, 1)[0] : sessionsArray?.splice(sessionIndex, 1)[0];

      // Create updated chatSessions object
      const updatedChatSessions: SessionsByDate =
        dateKey === today
          ? {
              ...project.chatSessions,
              [today]:
                todaysSessionsArray.length == 0
                  ? [{ ...removedSession, updatedAt: today, title: newTitle }]
                  : [{ ...removedSession, updatedAt: today, title: newTitle }, ...(todaysSessionsArray || [])]
            }
          : {
              [today]: [...(todaysSessionsArray || [])],
              ...project.chatSessions,
              [today]: [{ ...removedSession, updatedAt: today, title: newTitle }, ...(todaysSessionsArray || [])],
              [dateKey]: sessionsArray
            };

      // If the date key's sessions array is empty, remove that date key
      if (sessionsArray.length === 0) {
        delete updatedChatSessions[dateKey];
      }

      // Update the project with new chatSessions
      categorizedProjectsArray[projectIndex] = {
        ...categorizedProjectsArray[projectIndex],
        chatSessions: updatedChatSessions
      };

      const updatedProjects: CategorizedProjects = {
        ...projects,
        [categoryToUse]: categorizedProjectsArray
      };

      dispatch({ type: 'SET_PROJECTS', payload: updatedProjects });
    } catch (error) {
      console.error('Error renaming session:', error);
    }
  };

  /**
   * Renames a project and updates the local state accordingly
   * @param {number} projectIndex - The index of the project to rename in its category array
   * @param {string} newTitle - The new title to assign to the project
   * @param {string} [category] - Optional category of the project (defaults to 'GENERAL')
   * @returns {Promise<void>} A promise that resolves when the project is renamed
   * @throws {Error} If there's an error renaming the project
   * @description This function:
   * 1. Calls the API to rename the project
   * 2. Shows a success notification
   * 3. Updates the local state by updating the project name in the specified category
   * 4. Shows an error notification if the rename operation fails
   */
  const renameProjectItem = async (projectIndex: number, newTitle: string, category?: string) => {
    try {
      const projects = state.projects as CategorizedProjects;
      const categoryToUse = category || 'GENERAL';
      const project = projects[categoryToUse as keyof CategorizedProjects][projectIndex];
      await renameProject(project.id, newTitle);
      notification.success({
        message: 'Project Renamed',
        placement: 'topRight'
      });

      const categorizedProjectArray = [...projects[categoryToUse as keyof CategorizedProjects]];
      categorizedProjectArray[projectIndex] = {
        ...categorizedProjectArray[projectIndex],
        name: newTitle
      };

      const updatedProjects: CategorizedProjects = {
        ...projects,
        [categoryToUse]: categorizedProjectArray
      };
      dispatch({ type: 'SET_PROJECTS', payload: updatedProjects });

      // Update currentSessionId if the renamed project is the current one
      if (state.currentSessionId?.project?.id === project.id) {
        dispatch({
          type: 'SET_CURRENT_SESSION_ID',
          payload: {
            ...state.currentSessionId,
            project: {
              ...state.currentSessionId.project,
              name: newTitle
            }
          }
        });
      }
    } catch (error) {
      console.error('Error renaming project:', error);
    }
  };
  /**
   * Sets the search value for filtering sessions
   * @param {string} value - The search value to filter sessions by
   */
  const setSearchValue = (value: string) => {
    dispatch({ type: 'SET_SEARCH_VALUE', payload: value });
  };
  const scrollToBottom = () => {
    scroller.scrollTo('bottom', {
      duration: 800,
      smooth: true,
      containerId: 'ContainerElementID'
    });
  };
  /**
   * Selects a chat session and loads its messages and bookmarks
   * @param {string} sessionId - The ID of the session to select
   * @param index
   * @param session
   * @param category
   * @param project
   */
  const selectSession = async (
    sessionId: string,
    index?: number,
    session?: Session,
    category?: string,
    project?: GroupedSessionsProject
  ) => {
    dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isMessageLoading: true } });
    dispatch({
      type: 'SET_CURRENT_SESSION_ID',
      payload: {
        id: sessionId,
        metadata: session?.metadata || {},
        updatedAt: session?.updatedAt || new Date().toISOString(),
        index: index || 0,
        category: category || 'GENERAL',
        project: project || null
      }
    });

    // Load session messages

    const { messages, reports }: any = await getAllChatsBySessionId(sessionId);
    dispatch({ type: 'SET_MESSAGES', payload: messages });
    dispatch({ type: 'CLEAR_AI_REPORT' });
    dispatch({ type: 'SET_REPORTS', payload: reports });
    // Load bookmarks for the selected session
    // const sessionBookmarks = messages.filter((message: Messages) => message.metadata?.isBookmarked);
    // dispatch({ type: 'SET_BOOKMARKS', payload: sessionBookmarks });

    dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isMessageLoading: false } });

    setTimeout(() => {
      scrollToBottom();
    }, 10);
    const urlParams = new URLSearchParams(window.location.search);
    const querySessionId = urlParams.get('sessionId');
    if (querySessionId && querySessionId !== sessionId) {
      clearQueryParams();
    }
  };

  /**
   * Creates a new chat session
   * @param projectId
   * @param {string} title - The title of the new session
   * @param {Metadata} [metadata] - Optional metadata for the session
   * @returns {Promise<Session>} The newly created session
   */
  const createNewSession = async (projectId: string, title: string, metadata?: Metadata) => {
    try {
      return await createSession(projectId, title, metadata);
    } catch (error) {
      console.error('Error creating new session:', error);
      throw error;
    }
  };

  const handleNewConversation = async (selectedProject?: GroupedSessionsProject) => {
    dispatch({ type: 'SET_BOOKMARKS', payload: [] });
    if (selectedProject) {
      dispatch({
        type: 'SET_LOADING',
        payload: { ...state.isLoading, isSessionLoading: true, isMessageLoading: true }
      });
      const newSession = await createNewSession(selectedProject?.id, selectedProject?.name, {});
      if (newSession) {
        const today = new Date().toISOString().split('T')[0];
        const updatedProjects: CategorizedProjects = {
          ...state.projects,
          [selectedProject.category]: state.projects[selectedProject.category as keyof CategorizedProjects].map(
            (project) =>
              project.id === selectedProject?.id
                ? {
                    ...project,
                    chatSessions: {
                      [today]: project.chatSessions[today] || [],
                      ...project.chatSessions,
                      [today]: [newSession, ...(project.chatSessions[today] || [])]
                    }
                  }
                : project
          )
        };
        dispatch({ type: 'SET_ACTIVE_KEY', payload: '1' });
        dispatch({ type: 'SET_PROJECTS', payload: updatedProjects });
        await selectSession(newSession.id, 0, newSession, selectedProject?.category, selectedProject);
      }
      dispatch({
        type: 'SET_LOADING',
        payload: { ...state.isLoading, isSessionLoading: false, isMessageLoading: false }
      });
    } else {
      dispatch({
        type: 'CREATE_NEW_CONVERSATION',
        payload: {
          project: state.projects.GENERAL[0]
        }
      });
      //clear the current session id
      dispatch({ type: 'SET_CURRENT_SESSION_ID', payload: null });
    }
    dispatch({ type: 'SET_ACTIVE_KEY', payload: '1' });
  };

  /**
   * Creates a new chat session
   * @param {string} title - The title of the new session
   * @param {Metadata} [metadata] - Optional metadata for the session
   * @param category
   * @param projectParams
   * @returns {Promise<Session>} The newly created session
   */
  const createNewProject = async (category: string, projectParams: CreateProjectParams) => {
    try {
      const newProject = await createProject(category, projectParams);
      return newProject;
    } catch (error) {
      console.error('Error creating new session:', error);
      throw error;
    }
  };

  /**
   * Sends a message and handles the streaming response
   * @param {string} content - The message content to send
   * @param mode
   * @param {Metadata} [metadata] - Optional metadata for the message
   * @param {boolean} [usePerplexity] - Whether to use Perplexity for web search
   * @param {string} [queryType] - The type of query to send
   * @param files
   */
  const sendMessage = async (content: string, mode: string, metadata?: Metadata, files?: SessionFile[] | null) => {
    dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isStreaming: true } });

    try {
      let sessionId = state.currentSessionId?.id;
      let newSession;
      // If no current session, create a new one
      if (!sessionId) {
        const projectId = state.currentSessionId?.project?.id || state.projects.GENERAL[0].id;
        newSession = await createNewSession(projectId, content, metadata || {});
        sessionId = newSession.id;
      }

      const userMessage: Messages = {
        id: Date.now().toString(),
        type: 'human',
        content,
        chatSessionId: sessionId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),

        metadata: {
          isBookmarked: false,
          files: files?.map((file) => file.filename) || []
        }
      };

      // Track message sent
      trackChatMessageSent(window.location.pathname, sessionId, userMessage.id);

      dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
      scrollToBottom();
      const token = await getAccessTokenSilently();
      abortControllerRef.current = new AbortController();
      const stream = await postChatQuery(
        sessionId,
        content,
        token,
        state.promptAreaData.promptOptions[mode] || state.promptAreaData.promptOptions[0],
        abortControllerRef.current,
        files?.map((file) => file.id) || []
      );

      const reader = stream.getReader();
      const decoder = new TextDecoder('utf-8', { fatal: false });
      let isStreaming = true;
      let currentEvent = '';
      let currentData = '';
      let backendMessageId = '';
      let accumulatedContent = '';
      let currentEventType = '';
      let currentRunId = '';
      const baseMessageId = Date.now().toString();

      while (isStreaming) {
        const { done, value } = await reader.read();
        if (done) {
          isStreaming = false;
          break;
        }

        try {
          // Decode the chunk with UTF-8
          const chunkText = decoder.decode(value, { stream: true });

          // Process each line in the chunk
          const lines = chunkText.split('\n');
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.replace('event: ', '').trim();
            } else if (line.startsWith('data: ')) {
              currentData = line.replace('data: ', '').trim();

              // Process the event when we have both event and data
              if (currentEvent && currentData) {
                try {
                  const payload = JSON.parse(currentData);

                  switch (currentEvent) {
                    case 'start':
                      if (payload.messageId) {
                        backendMessageId = payload.messageId;
                      }

                      break;

                    case 'toolUse':
                      if (payload.content) {
                        if (currentEventType === 'toolUse') {
                          accumulatedContent += payload.content;
                          dispatch({ type: 'UPDATE_INTERMEDIATE_MESSAGE', payload: { content: accumulatedContent } });
                        } else {
                          accumulatedContent = payload.content;
                          currentEventType = 'toolUse';
                          const newMessage: Messages = {
                            id: backendMessageId || baseMessageId,
                            type: 'toolUse',
                            content: payload.content,
                            chatSessionId: sessionId,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),

                            metadata: {
                              isBookmarked: false,
                              ...(metadata as Metadata)
                            }
                          };
                          dispatch({ type: 'ADD_INTERMEDIATE_MESSAGE', payload: { message: newMessage } });
                        }

                        scrollToBottom();
                      }
                      break;

                    case 'aiThinking':
                      if (payload.content) {
                        if (currentEventType === 'aiThinking') {
                          accumulatedContent += payload.content;
                          dispatch({ type: 'UPDATE_INTERMEDIATE_MESSAGE', payload: { content: accumulatedContent } });
                        } else {
                          accumulatedContent = payload.content;
                          currentEventType = 'aiThinking';
                          const newMessage: Messages = {
                            id: backendMessageId || baseMessageId,
                            type: 'aiThinking',
                            content: payload.content,
                            chatSessionId: sessionId,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                          };
                          dispatch({ type: 'ADD_INTERMEDIATE_MESSAGE', payload: { message: newMessage } });
                        }

                        scrollToBottom();
                      }
                      break;

                    case 'assistantResponse':
                      if (payload.content) {
                        if (currentEventType === 'assistantResponse') {
                          accumulatedContent += payload.content;
                          dispatch({
                            type: 'UPDATE_MESSAGE',
                            payload: { id: payload.message_id, content: accumulatedContent }
                          });
                        } else {
                          accumulatedContent = payload.content;
                          currentEventType = 'assistantResponse';
                          dispatch({ type: 'CLEAR_INTERMEDIATE_MESSAGES' });
                          console.log('payload.messageId', payload.message_id);
                          const newMessage: Messages = {
                            id: payload.message_id,
                            type: 'assistantResponse',
                            content: payload.content,
                            chatSessionId: sessionId,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),

                            metadata: {
                              isBookmarked: false,
                              ...(metadata as Metadata)
                            }
                          };

                          dispatch({ type: 'ADD_MESSAGE', payload: newMessage });
                        }

                        scrollToBottom();
                      }
                      break;
                    case 'aiReport':
                      if (payload.content) {
                        currentEventType = 'aiReport';
                        if (currentRunId !== payload.run_id) {
                          dispatch({ type: 'CLEAR_AI_REPORT' });
                          dispatch({ type: 'UPDATE_AI_REPORT_RUN_ID', payload: payload.run_id });
                          currentRunId = payload.run_id;
                        }
                        dispatch({ type: 'UPDATE_AI_REPORT_CONTENT', payload: { content: payload.content } });
                      }
                      break;
                    case 'end':
                      currentEventType = 'end';
                      dispatch({ type: 'CLEAR_INTERMEDIATE_MESSAGES' });
                      dispatch({
                        type: 'SET_LOADING',
                        payload: { ...state.isLoading, isStreaming: false }
                      });

                      setTimeout(() => {
                        scrollToBottom();
                      }, 10);

                      if (newSession) {
                        const projects = state.projects as CategorizedProjects;
                        const categoryToUse = state.currentSessionId?.project?.category || 'GENERAL';

                        dispatch({
                          type: 'SET_CURRENT_SESSION_ID',
                          payload: {
                            id: newSession.id,
                            metadata: metadata || {},
                            updatedAt: newSession.updatedAt || new Date().toISOString(),
                            index: 0,
                            category: categoryToUse,
                            project: state.currentSessionId?.project || projects.GENERAL[0] || null
                          }
                        });

                        const today = new Date().toISOString().split('T')[0];
                        const updatedProjects: CategorizedProjects = {
                          ...projects,
                          [categoryToUse]: projects[categoryToUse as keyof CategorizedProjects].map((project) =>
                            project.id === newSession?.project?.id
                              ? {
                                  ...project,
                                  chatSessions: {
                                    [today]: project.chatSessions[today] || [],
                                    ...project.chatSessions,
                                    [today]: [newSession, ...(project.chatSessions[today] || [])]
                                  }
                                }
                              : project
                          )
                        };

                        dispatch({ type: 'SET_PROJECTS', payload: updatedProjects });
                      }
                      if (state.currentSessionId && !newSession) {
                        const today = new Date().toISOString().split('T')[0];
                        const categoryToUse = state.currentSessionId?.project?.category || 'GENERAL';

                        if (state.currentSessionId.index !== -1) {
                          const sessionDate = new Date(state.currentSessionId.updatedAt).toISOString().split('T')[0];
                          const projectIndex = state.projects[categoryToUse as keyof CategorizedProjects].findIndex(
                            (project) => project.id == state.currentSessionId?.project?.id
                          );
                          const projectChatSessions =
                            state.projects[categoryToUse as keyof CategorizedProjects][projectIndex].chatSessions[
                              sessionDate
                            ];
                          if (projectChatSessions) {
                            const dateSessionsCopy = [...projectChatSessions];
                            const removedSession = dateSessionsCopy.splice(state.currentSessionId.index, 1)[0];

                            const updatedSession = {
                              ...removedSession,
                              updatedAt: new Date().toISOString()
                            };

                            if (sessionDate !== today) {
                              const projects = state.projects as CategorizedProjects;
                              const updatedProjects: CategorizedProjects = {
                                ...projects,
                                [categoryToUse]: projects[categoryToUse as keyof CategorizedProjects].map((project) =>
                                  project.id === state.currentSessionId?.project?.id
                                    ? {
                                        ...project,
                                        chatSessions: {
                                          [today]: project.chatSessions[today] || [],
                                          ...project.chatSessions,
                                          [today]: [updatedSession, ...(project.chatSessions[today] || [])],
                                          [sessionDate]: project.chatSessions[sessionDate].filter(
                                            (session: Session) => session.id !== state.currentSessionId?.id
                                          )
                                        }
                                      }
                                    : project
                                )
                              };
                              dispatch({ type: 'SET_PROJECTS', payload: updatedProjects });
                            } else {
                              // If same day, just update the session
                              dateSessionsCopy.splice(0, 0, updatedSession);

                              const projects = state.projects as CategorizedProjects;
                              const updatedProjects: CategorizedProjects = {
                                ...projects,
                                [categoryToUse]: projects[categoryToUse as keyof CategorizedProjects].map((project) =>
                                  project.id === state.currentSessionId?.project?.id
                                    ? {
                                        ...project,
                                        chatSessions: {
                                          ...project.chatSessions,
                                          [sessionDate]: dateSessionsCopy
                                        }
                                      }
                                    : project
                                )
                              };
                              dispatch({ type: 'SET_PROJECTS', payload: updatedProjects });
                            }
                          }
                        }
                      }
                      break;
                  }
                } catch (parseError) {
                  console.error('Error parsing data:', parseError);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error processing chunk:', error);
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Stream aborted by user');
            isStreaming = false;
            break;
          }
        }
      }

      dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isStreaming: false } });

      // Track successful message delivery
      const currentSession = state.currentSessionId?.id || 'unknown';
      const lastMessage = state.messages[state.messages.length - 1];
      trackChatMessageDelivered(window.location.pathname, currentSession, lastMessage?.id || 'unknown', true);
    } catch (error) {
      console.error('Error in sendMessage:', error);
      dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isStreaming: false } });

      // Track failed message delivery
      const currentSession = state.currentSessionId?.id || 'unknown';
      trackChatMessageDelivered(window.location.pathname, currentSession, 'unknown', false);
    }
  };

  /**
   * Stops the current streaming response
   */
  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isStreaming: false } });
    }
  };

  /**
   * Bookmarks or unbookmarks a message
   * @param {string} messageId - The ID of the message to bookmark/unbookmark
   * @param {object} params - Parameters for the bookmark operation
   * @param {boolean} [params.isBookmarked] - Whether the message should be bookmarked (true) or unbookmarked (false)
   * @param params.metadata
   * @param params.metadata.isBookmarked
   * @returns {Promise<void>}
   */
  const bookmarkMessage = async (messageId: string, params: { metadata: { isBookmarked?: boolean } }) => {
    if (!state.currentSessionId) return;

    try {
      await updateMessageAsBookmark(messageId, params);

      // Update the message in the messages array

      const updatedMessages: Messages[] = state.messages.map((message) =>
        message.id === messageId
          ? {
              ...message,
              metadata: {
                ...message.metadata,
                isBookmarked: params.metadata.isBookmarked ?? false
              }
            }
          : message
      );
      dispatch({ type: 'SET_MESSAGES', payload: updatedMessages });

      // Update bookmarks array based on the updated messages
      const updatedBookmarks = updatedMessages.filter((message) => message.metadata?.isBookmarked === true);
      dispatch({ type: 'SET_BOOKMARKS', payload: updatedBookmarks });
    } catch (error) {
      console.error('Error bookmarking message:', error);
    }
  };

  const groupByDate = (chatSessionsArr: Session[]): SessionsByDate => {
    const groupedSessions: SessionsByDate = {};

    chatSessionsArr.forEach((session) => {
      const date = new Date(session.updatedAt).toISOString().split('T')[0];

      if (!groupedSessions[date]) {
        groupedSessions[date] = [];
      }

      groupedSessions[date].push(session);
    });

    // Sort sessions within each date group by updatedAt in descending order
    Object.keys(groupedSessions).forEach((date) => {
      groupedSessions[date].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    });

    return groupedSessions;
  };

  const groupData = (data: Project[]): CategorizedProjects => {
    const result: CategorizedProjects = {
      GENERAL: [],
      DEAL: [],
      SUPPLIER: [],
      CLIENT: []
    };

    data?.forEach((project) => {
      const { category, id, name, chatSessions } = project;

      // Push the grouped project into its category
      if (category in result) {
        (result[category as keyof CategorizedProjects] as GroupedSessionsProject[]).push({
          id,
          name,
          category,
          userId: project.userId,
          chatSessions: groupByDate(project.chatSessions),
          createdAt: project.createdAt,
          updatedAt: project.updatedAt,
          description: project.description,
          isArchived: project.isArchived,
          contractId: project.contractId,
          contract: project.contract
        });
      }
    });

    return result;
  };
  const fetchSuppliers = async () => {
    try {
      const vendorData = await getVendorInfo();
      const options: { value: string; label: string }[] = vendorData.map((vendor: { name: string }) => ({
        value: vendor.name,
        label: vendor.name
      }));
      const assistantsData = await getAllAssistants();
      const promptOptions: { [key: string]: string } = {};
      assistantsData.forEach((assistant: { label: string; value: string }) => {
        promptOptions[assistant.label] = assistant.value;
      });
      dispatch({ type: 'SET_PROMPT_AREA_DATA', payload: { vendor: options, promptOptions } });
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };
  /**
   * Loads the initial session and sets up the chat state
   */
  const loadInitialSession = async () => {
    const params = new URLSearchParams(window.location.search);
    try {
      dispatch({
        type: 'SET_LOADING',
        payload: { ...state.isLoading, isSessionLoading: true, isMessageLoading: true }
      });
      await fetchSuppliers();
      const contractId = params.get('contractId') || state.embeddedWindowState.data.ContractId;
      const sessionId = params.get('sessionId');
      const apiRes: Project[] = await getAllProjects(
        state.embeddedWindowState.isEmbeddedWindowOpen || state.embeddedWindowState.isDrawerOpen
          ? { type: state.embeddedWindowState.type, data: state.embeddedWindowState.data }
          : undefined
      );
      const projectsAndSessions: CategorizedProjects = groupData(apiRes);

      if (projectsAndSessions) {
        dispatch({ type: 'SET_PROJECTS', payload: projectsAndSessions });

        if (contractId && sessionId) {
          const dealProjects = projectsAndSessions.DEAL || [];
          let foundSession: Session | null = null;
          let foundProject: GroupedSessionsProject | null = null;
          let sessionIndex = -1;

          for (const project of dealProjects) {
            for (const [date, sessions] of Object.entries(project.chatSessions)) {
              const session = sessions.find((s) => s.id === sessionId);
              if (session) {
                foundSession = session;
                foundProject = project;
                sessionIndex = sessions.indexOf(session);
                break;
              }
            }
            if (foundSession) break;
          }

          if (foundSession && foundProject) {
            await selectSession(foundSession.id, sessionIndex, foundSession, 'DEAL', foundProject);
          }
        } else if (state.embeddedWindowState.isEmbeddedWindowOpen || state.embeddedWindowState.isDrawerOpen) {
          const firstSession =
            projectsAndSessions[state.embeddedWindowState.type][0]?.chatSessions[
              Object.keys(projectsAndSessions[state.embeddedWindowState.type][0].chatSessions)[0]
            ]?.[0];
          if (firstSession) {
            await selectSession(
              firstSession.id,
              0,
              firstSession,
              state.embeddedWindowState.type,
              projectsAndSessions[state.embeddedWindowState.type][0]
            );
          } else {
            await handleNewConversation(projectsAndSessions[state.embeddedWindowState.type][0]);
          }
        }
        dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isSessionLoading: false } });
      } else {
        message.error('Failed to load conversations');
      }
    } catch (error) {
      console.error('Error loading initial session:', error);
    } finally {
      dispatch({
        type: 'SET_LOADING',
        payload: { ...state.isLoading, isSessionLoading: false, isMessageLoading: false }
      });
    }
  };

  // Computed values

  const filteredSessions = Object.entries(state.sessions).flatMap(([date, sessions]) =>
    sessions.filter((session) => session.title.toLowerCase().includes(state.searchValue.toLowerCase()))
  );

  const filteredProjects = Object.entries(state.projects).reduce(
    (acc, [category, projects]) => ({
      ...acc,
      [category]: projects
        .map((project: GroupedSessionsProject) => {
          const filteredSessionsByDate: SessionsByDate = {};
          Object.entries(project.chatSessions).forEach(([date, sessions]) => {
            const filteredSessions = sessions.filter((session: Session) =>
              session?.title?.toLowerCase()?.includes(state?.searchValue?.toLowerCase())
            );
            if (filteredSessions.length > 0) {
              filteredSessionsByDate[date] = filteredSessions;
            }
          });
          return {
            ...project,

            chatSessions: filteredSessionsByDate
          };
        })
        .filter((project: GroupedSessionsProject) => {
          // For GENERAL category, show only if it has chatSessions
          if (project?.category === ProjectCategory.GENERAL) {
            return Object.keys(project.chatSessions).length > 0;
          }
          // For other categories (DEAL, etc.), show if:
          // 1. There's no search value, OR
          // 2. There are matching chatSessions for the search value
          return !state.searchValue.trim() || Object.keys(project.chatSessions).length > 0;
        })
    }),
    {} as CategorizedProjects
  );

  const searchOptions = state.searchValue.trim()
    ? filteredSessions.map((session) => ({
        value: session.title,
        label: session.title
      }))
    : [];

  const openEmbeddedWindow = (
    type: 'DEAL' | 'CLIENT' | 'SUPPLIER',
    data: { ContractId: number | null },
    typeOfWindow: 'embedded' | 'horizontal'
  ) => {
    if (typeOfWindow === 'horizontal') {
      dispatch({
        type: 'SET_EMBEDDED_WINDOW_STATE',
        payload: { isEmbeddedWindowOpen: false, isDrawerOpen: true, type, data }
      });
    } else {
      dispatch({
        type: 'SET_EMBEDDED_WINDOW_STATE',
        payload: { isEmbeddedWindowOpen: true, isDrawerOpen: false, type, data }
      });
    }
  };

  const closeEmbeddedWindow = () => {
    dispatch({
      type: 'SET_EMBEDDED_WINDOW_STATE',
      payload: { isEmbeddedWindowOpen: false, isDrawerOpen: false, type: 'DEAL', data: { ContractId: null } }
    });
    dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isSessionLoading: false } });
  };

  const createNewDealProject = async (ContractId: number) => {
    dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isSessionLoading: true, isMessageLoading: true } });
    const newProjectResponse = await createNewProject('DEAL', {
      deal: {
        contractId: ContractId
      }
    });

    // Create a new project object with grouped sessions
    const today = new Date().toISOString().split('T')[0];
    const newProject: GroupedSessionsProject = {
      id: newProjectResponse.id,
      contractId: ContractId,
      name: newProjectResponse.name,
      category: newProjectResponse.category,
      userId: newProjectResponse.userId,
      chatSessions: { [today]: newProjectResponse.chatSessions },
      createdAt: newProjectResponse.createdAt,
      updatedAt: newProjectResponse.updatedAt,
      contract: newProjectResponse.contract
    };

    // Update the projects state with the new project
    const projects = state.projects as CategorizedProjects;
    const updatedProjects: CategorizedProjects = {
      ...projects,
      DEAL: [newProject, ...projects.DEAL]
    };
    dispatch({ type: 'SET_PROJECTS', payload: updatedProjects });

    await selectSession(
      newProjectResponse.chatSessions[0].id,
      0,
      newProjectResponse.chatSessions[0],
      'DEAL',
      newProject
    );
    dispatch({
      type: 'SET_LOADING',
      payload: { ...state.isLoading, isSessionLoading: false, isMessageLoading: false }
    });
  };

  const openRenameDialog = (dialogState: RenameDialogState) => {
    dispatch({ type: 'OPEN_RENAME_DIALOG', payload: dialogState });
  };

  const closeRenameDialog = () => {
    dispatch({ type: 'CLOSE_RENAME_DIALOG' });
  };

  const openRemoveDialog = (dialogState: RemoveDialogState) => {
    dispatch({ type: 'OPEN_REMOVE_DIALOG', payload: dialogState });
  };

  const closeRemoveDialog = () => {
    dispatch({ type: 'CLOSE_REMOVE_DIALOG' });
  };

  const openNewDealProjectDialog = () => {
    dispatch({ type: 'OPEN_NEW_DEAL_PROJECT_DIALOG' });
  };

  const closeNewDealProjectDialog = () => {
    dispatch({ type: 'CLOSE_NEW_DEAL_PROJECT_DIALOG' });
  };

  const setIsDocumentMode = (isDocumentMode: boolean) => {
    dispatch({ type: 'SET_IS_DOCUMENT_MODE', payload: isDocumentMode });
    setTimeout(() => {
      scrollToBottom();
    }, 10);
  };

  const setPromptMode = (promptMode: string) => {
    dispatch({ type: 'SET_PROMPT_MODE', payload: promptMode });
  };
  const setReport = (reportContent: string) => {
    dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isReportLoading: true } });

    dispatch({ type: 'CLEAR_AI_REPORT' });
    dispatch({ type: 'UPDATE_AI_REPORT_CONTENT', payload: { content: reportContent } });
    setTimeout(() => {
      dispatch({ type: 'SET_LOADING', payload: { ...state.isLoading, isReportLoading: false } });
    }, 10);
  };
  const updateReport = (reportType: string, reportContent: string) => {
    dispatch({ type: 'UPDATE_REPORTS', payload: { reportType, reportContent } });
  };
  return {
    state,
    dispatch,
    // SideNav
    setSearchValue,
    selectSession,
    filteredSessions,
    filteredProjects,
    searchOptions,
    // Conversations
    sendMessage,
    stopStreaming,
    // Bookmarks
    bookmarkMessage,
    // Initialization
    loadInitialSession,
    deleteSessionItem,
    deleteProjectItem,
    renameSessionItem,
    renameProjectItem,
    createNewProject,
    setActiveKey: (key: string) => dispatch({ type: 'SET_ACTIVE_KEY', payload: key }),
    scrollToBottom: () => scrollToBottom(),
    openEmbeddedWindow,
    closeEmbeddedWindow,
    createNewDealProject,
    createNewSession,
    openRenameDialog,
    closeRenameDialog,
    openRemoveDialog,
    closeRemoveDialog,
    openNewDealProjectDialog,
    handleNewConversation,
    clearQueryParams,
    closeNewDealProjectDialog,
    setIsDocumentMode,
    setPromptMode,
    setReport,
    updateReport
  };
};
